import { useEffect, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import client from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { FeedPathTag } from "@/components/FeedPathTag";
import { Image as ImageIcon, RefreshCw, Trash2, Upload } from "lucide-react";

const DEFAULT_MAX_SIZE_MB = 5;
const DEFAULT_ALLOWED_FORMATS = ["jpeg", "png", "webp", "gif"];
const FORMAT_MIME = { jpeg: "image/jpeg", png: "image/png", webp: "image/webp", gif: "image/gif" };

const LOGO_FIELDS = ["logo_remote_url", "logo_source", "logo_filename", "local_logo_url"];
const pickLogo = (d) => Object.fromEntries(LOGO_FIELDS.map((k) => [k, d?.[k] || ""]));

/**
 * "Dealer Logo" card — one image, one size, imported from the feed's LogoURL
 * (Settings → Dealer Feed Import → "Import logos") or uploaded manually; see
 * backend/dealer_logos.py. Keeps its own logo state, seeded from the dealer
 * once: the local_logo_url in `dealer` is a signed URL only the summary and
 * this card's own endpoints produce, so a dealer document bubbled up by a
 * different card must never overwrite it.
 */
export function DealerLogoCard({ dealer, dealerId, canManage = false }) {
  const { t } = useI18n();
  const [logo, setLogo] = useState(() => pickLogo(dealer));
  const [busy, setBusy] = useState(false);
  const [maxSizeMb, setMaxSizeMb] = useState(DEFAULT_MAX_SIZE_MB);
  const [allowedFormats, setAllowedFormats] = useState(DEFAULT_ALLOWED_FORMATS);
  const fileInputRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [sizeRes, formatsRes] = await Promise.all([
          client.get("/settings/photo_max_size_mb"),
          client.get("/settings/photo_allowed_formats"),
        ]);
        if (sizeRes.data?.value != null) setMaxSizeMb(Number(sizeRes.data.value));
        if (Array.isArray(formatsRes.data?.value)) setAllowedFormats(formatsRes.data.value);
      } catch {
        // Not readable/configured — keep the defaults.
      }
    })();
  }, []);

  const run = async (request, okKey, failKey) => {
    setBusy(true);
    try {
      const { data } = await request();
      setLogo(pickLogo(data));
      toast.success(t(okKey));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t(failKey));
    } finally {
      setBusy(false);
    }
  };

  const onFileChosen = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > maxSizeMb * 1024 * 1024) {
      toast.error(`${t("common.fileTooLarge")} (max ${maxSizeMb} MB)`);
      return;
    }
    if (allowedFormats.length > 0 && !allowedFormats.some((f) => FORMAT_MIME[f] === file.type)) {
      toast.error(`Unsupported format — allowed: ${allowedFormats.map((f) => f.toUpperCase()).join(", ")}`);
      return;
    }
    const form = new FormData();
    form.append("file", file);
    run(
      () => client.post(`/dealers/${dealerId}/logo`, form, { headers: { "Content-Type": "multipart/form-data" } }),
      "dealership.logo.uploaded", "dealership.logo.uploadFailed",
    );
  };

  const remove = () => {
    if (!window.confirm(t("dealership.logo.deleteConfirm"))) return;
    run(() => client.delete(`/dealers/${dealerId}/logo`), "dealership.logo.deleted", "dealership.logo.deleteFailed");
  };

  const refresh = () => {
    if (!window.confirm(t("dealership.logo.refreshConfirm"))) return;
    run(
      () => client.post(`/dealers/${dealerId}/logo/refresh-from-feed`),
      "dealership.logo.refreshed", "dealership.logo.refreshFailed",
    );
  };

  return (
    <Card className="mb-6 p-6" data-testid="dealer-logo-card">
      <input
        ref={fileInputRef}
        type="file"
        accept={allowedFormats.map((f) => FORMAT_MIME[f]).filter(Boolean).join(",") || "image/*"}
        className="hidden"
        onChange={onFileChosen}
      />
      <div className="mb-4 flex items-center gap-2">
        <ImageIcon className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-lg font-semibold">{t("dealership.logo.title")}</h2>
        <FeedPathTag path="Dealer.LogoURL" />
      </div>

      <div className="flex flex-wrap items-center gap-6">
        <div className="flex h-24 w-48 items-center justify-center rounded-md border border-border bg-secondary/40">
          {logo.local_logo_url ? (
            <img
              data-testid="dealer-logo-img"
              src={`${process.env.REACT_APP_BACKEND_URL}${logo.local_logo_url}`}
              alt={t("dealership.logo.title")}
              className="max-h-full max-w-full object-contain"
            />
          ) : (
            <span className="text-xs text-muted-foreground">{t("dealership.logo.none")}</span>
          )}
        </div>

        <div className="space-y-2">
          {logo.logo_filename && (
            <p className="font-mono text-[10px] text-muted-foreground" data-testid="dealer-logo-filename">
              {logo.logo_filename}
            </p>
          )}
          {canManage && (
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline" size="sm" className="gap-1.5 text-xs" disabled={busy}
                data-testid="dealer-logo-upload" onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="h-3 w-3" />
                {logo.local_logo_url ? t("dealership.logo.replace") : t("dealership.logo.upload")}
              </Button>
              {logo.local_logo_url && (
                <Button
                  variant="outline" size="sm" className="gap-1.5 text-xs text-destructive" disabled={busy}
                  data-testid="dealer-logo-delete" onClick={remove}
                >
                  <Trash2 className="h-3 w-3" />
                  {t("dealership.logo.delete")}
                </Button>
              )}
              <Button
                variant="outline" size="sm" className="gap-1.5 text-xs" disabled={busy || !logo.logo_remote_url}
                title={!logo.logo_remote_url ? t("dealership.logo.noFeedLogo") : undefined}
                data-testid="dealer-logo-refresh" onClick={refresh}
              >
                <RefreshCw className="h-3 w-3" />
                {t("dealership.logo.refresh")}
              </Button>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
